import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/utils/styles';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded text-sm font-normal leading-none transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-azure-700 text-neutral-0 shadow hover:bg-azure-600 active:bg-azure-800',
        light:
          'bg-neutral-100 !text-neutral-900 hover:bg-neutral-300 active:bg-neutral-400',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        darkDestructive:
          'bg-secondary text-destructive shadow-sm hover:bg-secondary/80',
        success: 'bg-veridianSuccess-700 shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-neutral-800 shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        dark: 'bg-neutral-900 text-secondary-foreground shadow-sm hover:bg-neutral-800 active:bg-neutral-1000',
        darkLight:
          'bg-neutral-800 text-secondary-foreground shadow-sm hover:bg-neutral-700/50 active:bg-neutral-1000/20',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        burst:
          'bg-harvestGoldWarning-600 shadow-sm hover:bg-harvestGoldWarning-600/90',
      },
      size: {
        default: 'h-9 px-3 py-2',
        sm: 'h-8 rounded px-3 text-xs',
        lg: 'h-10 rounded px-8',
        xl: 'rounded p-4 text-base leading-none md:p-5',
        fw: 'h-9 w-full px-3 py-2',
        icon: 'size-9',
      },
      outline: {
        default: '',
        thin: 'border border-neutral-0',
        darkThin: 'border border-neutral-800',
        darkLightThin: 'border border-neutral-700',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      outline: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, outline, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, outline, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
